import {
  authCookieDomain,
  authCookieMaxAgeDays,
  authCookieName,
} from "../config/config";

const isBrowser =
  typeof window !== "undefined" && typeof document !== "undefined";

const CURRENT_USER_KEY = "currentUser";

type CookieOptions = {
  domain?: string;
  maxAgeDays?: number;
};

function buildAttributes(opts: CookieOptions): string {
  const parts = ["path=/", "samesite=lax"];
  if (typeof opts.maxAgeDays === "number") {
    parts.push(`max-age=${Math.floor(opts.maxAgeDays * 86400)}`);
  }
  if (opts.domain) {
    parts.push(`domain=${opts.domain}`);
  }
  if (
    typeof location !== "undefined" &&
    location.protocol === "https:"
  ) {
    parts.push("secure");
  }
  return parts.join("; ");
}

export class Storage {
  static getString(key: string): string | null {
    if (!isBrowser) return null;
    return localStorage.getItem(key);
  }

  static delete(key: string) {
    if (!isBrowser) return;
    localStorage.removeItem(key);
  }

  static setString(key: string, value: string) {
    if (!isBrowser) return;
    localStorage.setItem(key, value);
  }

  static getCurrentUserToken(): string | null | undefined {
    const userString = Storage.getString(CURRENT_USER_KEY);
    const user = userString ? JSON.parse(userString) : null;
    return user?.accessToken;
  }

  static setBoolean(key: string, value: boolean) {
    if (!isBrowser) return;
    localStorage.setItem(key, value.toString());
  }

  static getBoolean(key: string): boolean {
    if (!isBrowser) return false;
    return localStorage.getItem(key) === "true";
  }

  // ---------- Cookie primitives ----------

  static setCookie(key: string, value: string, opts: CookieOptions) {
    if (!isBrowser) return;
    const encoded = encodeURIComponent(value);
    document.cookie = `${key}=${encoded}; ${buildAttributes(opts)}`;

    if (import.meta.env.DEV) {
      // Guardrail: if the cookie isn't readable back after a write, the most likely cause
      // is a domain mismatch between apps/web and apps/business in local .env files.
      if (Storage.getCookie(key) === null) {
        console.warn(
          `[Storage.setCookie] shared auth cookie "${key}" not readable after write — ` +
            `VITE_AUTH_COOKIE_DOMAIN likely differs between apps.`,
        );
      }
    }
  }

  static getCookie(key: string): string | null {
    if (!isBrowser) return null;
    const cookies = document.cookie ? document.cookie.split("; ") : [];
    const prefix = `${key}=`;
    for (const c of cookies) {
      if (c.startsWith(prefix)) {
        const raw = c.slice(prefix.length);
        try {
          return decodeURIComponent(raw);
        } catch {
          return null;
        }
      }
    }
    return null;
  }

  static deleteCookie(key: string, opts: CookieOptions) {
    if (!isBrowser) return;
    // max-age=0 evicts the cookie. Attribute must include the same domain (or omit it
    // for host-only deletes) that was used on write — otherwise the browser leaves the
    // cookie in place.
    document.cookie = `${key}=; ${buildAttributes({ ...opts, maxAgeDays: 0 })}`;
  }

  // ---------- Auth-specific facade ----------
  //
  // Use these everywhere currentUser is touched. The cookie payload is a strict
  // whitelist: { id, email, accessToken, refreshToken, roles }. Profile data
  // (saved_places, badgeImages, businessOnboardingTasks, etc.) stays in localStorage
  // only — the cookie must stay well under the 4KB browser limit.

  static setCurrentUser(userJson: string) {
    if (!isBrowser) return;

    let parsed: any;
    try {
      parsed = JSON.parse(userJson);
    } catch {
      console.error("[Storage.setCurrentUser] invalid JSON payload");
      return;
    }

    if (
      !parsed ||
      typeof parsed.accessToken !== "string" ||
      parsed.accessToken.length === 0 ||
      typeof parsed.refreshToken !== "string" ||
      parsed.refreshToken.length === 0
    ) {
      console.error(
        "[Storage.setCurrentUser] missing required token fields; not persisting",
      );
      return;
    }

    localStorage.setItem(CURRENT_USER_KEY, userJson);

    const cookiePayload = JSON.stringify({
      id: parsed.id,
      email: parsed.email,
      accessToken: parsed.accessToken,
      refreshToken: parsed.refreshToken,
      roles: parsed.roles,
    });
    Storage.setCookie(authCookieName, cookiePayload, {
      domain: authCookieDomain,
      maxAgeDays: authCookieMaxAgeDays,
    });
  }

  static clearCurrentUser() {
    if (!isBrowser) return;

    localStorage.removeItem(CURRENT_USER_KEY);

    // Delete must match the Domain attribute used on write. When authCookieDomain
    // is configured, also issue a host-only delete to catch cookies left over from
    // local-dev (host-only writes) or earlier deploys with a different domain config.
    if (authCookieDomain) {
      Storage.deleteCookie(authCookieName, { domain: authCookieDomain });
    }
    Storage.deleteCookie(authCookieName, {});
  }

  static hydrateCurrentUserFromCookie() {
    if (!isBrowser) return;
    if (localStorage.getItem(CURRENT_USER_KEY)) return;

    const raw = Storage.getCookie(authCookieName);
    if (!raw) return;

    let parsed: any;
    try {
      parsed = JSON.parse(raw);
    } catch {
      Storage.clearCurrentUser();
      return;
    }

    if (
      !parsed ||
      typeof parsed.accessToken !== "string" ||
      parsed.accessToken.length === 0 ||
      typeof parsed.refreshToken !== "string" ||
      parsed.refreshToken.length === 0 ||
      parsed.roles == null ||
      typeof parsed.roles !== "object"
    ) {
      Storage.clearCurrentUser();
      return;
    }

    localStorage.setItem(CURRENT_USER_KEY, raw);
  }

  // Restore the shared portal auth cookie from localStorage if it's missing.
  // Used by the My Venues click handler to recover from the "logged out on portal,
  // still logged in on web" scenario. Strict only-if-missing semantics: never
  // overwrites a present cookie, never clears. No-op when localStorage is empty.
  static ensureCurrentUserCookie() {
    if (!isBrowser) return;
    if (Storage.getCookie(authCookieName) !== null) return;
    const userString = localStorage.getItem(CURRENT_USER_KEY);
    if (!userString) return;
    Storage.setCurrentUser(userString);
  }
}
